'use client';

import { ReactNode, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import TabsContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Tab from '@mui/material/Tab';
import classNames from 'classnames';
import { useLocale, useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';

import { useSymbolInfo } from '@/api/trading/useSymbolInfo';
import tabsStyles from '@/blocks/components/Controls/Tabs/styles.module.scss';
import { ChevronDown } from '@/blocks/components/Icons/Chevron';
import { formatAssetNameToTradingViewWidgetPropsFormat, ourLocaleToTwLocale } from '@/blocks/components/TW';
import { WithTrendColor } from '@/blocks/components/WithTrendColor';
import { FormatPercent } from '@/blocks/formatters';
import { useScrollTo } from '@/hooks/useScrollTo';
import buttons from '@/styles/button.module.scss';
import typography from '@/styles/typography.module.scss';

import { Asset as LocalAsset } from '@/app/[lang]/price-chart/[category]/types';

import styles from './styles.module.scss';

const TechnicalAnalysis = dynamic(
  () => import('react-ts-tradingview-widgets').then((w) => w.TechnicalAnalysis),
  {
    ssr: false,
  },
);

function ExpandButton(props: { sticky: boolean; expanded: boolean; onClick: () => void }) {
  const tr = useTranslations('pages.priceCharts.asset.sections.mainProfile.tabs.about');
  return (
    <div
      className={classNames(styles.aboutTabButtonContainer, props.sticky && props.expanded && styles.shadow)}
      onClick={props.onClick}
    >
      <button className={classNames(buttons.btn, buttons.btnS, buttons.btnIconRight, buttons.btnLink)}>
        {tr.rich(props.expanded ? 'showLess' : 'showMore')} {<ChevronDown />}
      </button>
    </div>
  );
}

CoinTabs.About = function CoinTabsAbout({
  asset,
  text,
  scrollToRef,
}: {
  asset: LocalAsset;
  text: string;
  scrollToRef: RefObject<HTMLElement>;
}) {
  const tr = useTranslations('pages.priceCharts.asset.sections.mainProfile.tabs.about');
  const shortText = useMemo(() => text.length <= 900, [text.length]);
  const [expanded, setExpanded] = useState(shortText);
  const { scrollToElement } = useScrollTo();
  const fakeElement = useRef<HTMLDivElement | null>(null);
  const [sticky, setSticky] = useState(false);

  const open = useCallback(() => {
    setExpanded(true);
    scrollToElement(scrollToRef);
  }, [scrollToElement]);

  const close = useCallback(() => {
    setExpanded(false);
    scrollToElement(scrollToRef, { behavior: 'instant' });
  }, [scrollToElement]);

  useEffect(() => {
    if (!(fakeElement.current instanceof HTMLElement)) {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      setSticky(!entry.isIntersecting);
    }, {});

    observer.observe(fakeElement.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={classNames(styles.aboutTab, expanded && styles.expanded)}
      onClick={() => {
        if (!expanded) {
          open();
        }
      }}
    >
      <h2 className={typography.h5}>{tr.rich('title', { asset: asset.description })}</h2>
      <div
        className={classNames(typography.bodyM, styles.aboutTabText)}
        dangerouslySetInnerHTML={{ __html: text }}
      ></div>
      <div ref={fakeElement}></div>
      {shortText ? null : (
        <ExpandButton
          sticky={sticky}
          expanded={expanded}
          onClick={() => {
            if (expanded) {
              close();
            }
          }}
        />
      )}
    </div>
  );
};

function IntervalsTable({ asset }: { asset: LocalAsset }) {
  const { data, error } = useSymbolInfo(asset.name);
  const tr = useTranslations('pages.priceCharts.asset.sections.mainProfile.tabs.statistics.intervalLabels');

  const getInterval = useCallback(
    (interval: string) => {
      const intervalsI18n: Record<string, ReactNode> = {
        '14d': tr.rich('14d'),
        '24h': tr.rich('24h'),
        '1h': tr.rich('1h'),
        '30d': tr.rich('30d'),
        '1y': tr.rich('1y'),
        '7d': tr.rich('7d'),
      };
      return intervalsI18n[interval] || interval;
    },
    [tr],
  );

  if (!data || !asset.supported || error) {
    return null;
  }

  return (
    <div className={styles.changeIntervals}>
      <table>
        {Object.entries(data.changes_percent).map(([interval, changePct]) => {
          return (
            <tr key={interval} className={typography.bodyM}>
              <th>{getInterval(interval)}</th>
              <td className={typography.semibold}>
                <WithTrendColor valueToCalculateTrend={changePct}>
                  <FormatPercent value={changePct} />
                </WithTrendColor>
              </td>
            </tr>
          );
        })}
      </table>
    </div>
  );
}

CoinTabs.Statistics = function CoinTabsStatistics({ asset }: { asset: LocalAsset }) {
  const locale = useLocale();

  return (
    <div className={classNames(typography.bodyM, styles.statsTabContent)}>
      <IntervalsTable asset={asset} />
      <div className={styles.statsTabContentWidget}>
        <TechnicalAnalysis
          copyrightStyles={{
            parent: {
              display: 'none',
            },
          }}
          isTransparent
          colorTheme="light"
          autosize
          locale={ourLocaleToTwLocale(locale)}
          symbol={formatAssetNameToTradingViewWidgetPropsFormat(asset.name)}
        />
      </div>
    </div>
  );
};

enum T {
  ABOUT = 'ABOUT',
  STATS = 'STATS',
}

export function CoinTabs({
  asset,
  assetProfileDescription,
}: {
  asset: LocalAsset;
  assetProfileDescription: string;
}) {
  const tr = useTranslations();
  const ref = useRef(null);
  const [selectedTab, setSelectedTab] = useState(assetProfileDescription ? T.ABOUT : T.STATS);

  if (!assetProfileDescription) {
    return <CoinTabs.Statistics asset={asset} />;
  }

  return (
    <TabsContext value={selectedTab}>
      <TabList
        className={classNames(tabsStyles.tabs)}
        variant={'scrollable'}
        scrollButtons={false}
        onChange={(e, tab) => setSelectedTab(tab)}
        ref={ref}
      >
        {!assetProfileDescription ? null : (
          <Tab
            disableRipple
            label={tr.rich('pages.priceCharts.asset.sections.mainProfile.tabs.about.label')}
            value={T.ABOUT}
          />
        )}
        <Tab
          disableRipple
          label={tr.rich('pages.priceCharts.asset.sections.mainProfile.tabs.stats.label')}
          value={T.STATS}
        />
      </TabList>
      <div className={styles.coinTabsSection}>
        {!assetProfileDescription ? null : (
          <TabPanel value={T.ABOUT}>
            <CoinTabs.About asset={asset} text={assetProfileDescription} scrollToRef={ref} />
          </TabPanel>
        )}
        <TabPanel value={T.STATS}>
          <CoinTabs.Statistics asset={asset} />
        </TabPanel>
      </div>
    </TabsContext>
  );
}
